'use client';

import { createContext, useContext, useState } from 'react';

import type { Duration, PreisMatrix, Tier } from './pricing-data';

/**
 * Geteilter Laufzeit-State für /preise. #628: Der `duration`-Toggle war zuvor in
 * `PricingSelector` gekapselt – die Vergleichstabelle (server-gerendert) konnte
 * ihn nicht lesen. Dieser Provider hebt den State auf eine Client-Grenze, die
 * BEIDE Bereiche (Selector + preis-tragende Compare-Zellen) umschließt. Die
 * statischen Sektionen bleiben server-gerendert und werden als `children`
 * durchgereicht → RSC-Grenze bleibt erhalten (nur Provider + interaktive
 * Leaf-Komponenten sind Client).
 */

interface PricingDurationValue {
  duration: Duration;
  setDuration: (d: Duration) => void;
  /** Erstpreise aus dem gültigen Preisstand; `null` = nicht abrufbar. */
  matrix: PreisMatrix | null;
  /** Das Paket, das der gültige Stand empfiehlt (`empfohlen_paket`, F4). */
  empfohlen: Tier | null;
}

const PricingDurationContext = createContext<PricingDurationValue | null>(null);

export function PricingProvider({
  matrix,
  empfohlen,
  children,
}: {
  matrix: PreisMatrix | null;
  empfohlen: Tier | null;
  children: React.ReactNode;
}) {
  const [duration, setDuration] = useState<Duration>('one-month');

  return (
    <PricingDurationContext.Provider
      value={{ duration, setDuration, matrix, empfohlen }}
    >
      {children}
    </PricingDurationContext.Provider>
  );
}

export function usePricingDuration(): PricingDurationValue {
  const ctx = useContext(PricingDurationContext);

  if (!ctx) {
    throw new Error('usePricingDuration must be used within <PricingProvider>');
  }

  return ctx;
}
